import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useLogout } from '../hooks/useLogout';
import media from '../style/media';
import { useGetAllVitalsQuery, useLazyGetVitalPdfQuery, useSendToSabaiMutation } from '../services/vitalsApi';
import { StorageKeys } from '../utils/storage';
import TopBar from '../components/TopBar';
import SettingsBars from '../components/SettingsBars';
import { useCameras } from '../hooks';
import { Activity, Heart, Wind, Brain, Droplet, Gauge, Clock, User, ClipboardList, Download, Share2, Mail, Phone, ChevronDown, ChevronUp } from 'lucide-react';
import { PageContainer, ContentWrapper, Header, Title, Subtitle, GlassCard } from '../style/layout';

const VitalsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const VitalsItem = styled.div`
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 24px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.02);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 20px rgba(0, 0, 0, 0.04);
    border-color: #e2e8f0;
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f8fafc;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #e2e8f0;
  background: white;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover:not(:disabled) {
    background: #f8fafc;
    color: #3b82f6;
    border-color: #cbd5e1;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 115%;
    left: 50%;
    transform: translateX(-50%);
    background-color: #334155;
    color: #fff;
    padding: 0.375rem 0.625rem;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease, visibility 0.2s ease, transform 0.2s ease;
    pointer-events: none;
    z-index: 10;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  
  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 5px;
    border-style: solid;
    border-color: #334155 transparent transparent transparent;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease, visibility 0.2s ease;
    pointer-events: none;
    z-index: 10;
  }

  &:hover:not(:disabled)::after {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(-2px);
  }

  &:hover:not(:disabled)::before {
    opacity: 1;
    visibility: visible;
  }
`;

const SabaiButton = styled.button`
  padding: 0.5rem 1rem;
  background: #8b5cf6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: #7c3aed;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ItemTitle = styled.h3`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  white-space: nowrap;
`;

const DateText = styled.span`
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
  white-space: nowrap;
  margin-right: 0.5rem;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;

  ${media.mobile`
    grid-template-columns: 1fr 1fr;
  `}
  
  ${media.tablet`
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  `}
`;

const MetricCard = styled.div<{ color?: string }>`
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  transition: all 0.2s ease;

  &:hover {
    background: #ffffff;
    border-color: ${props => props.color || '#3b82f6'};
    box-shadow: 0 4px 12px ${props => (props.color ? `${props.color}15` : 'rgba(59, 130, 246, 0.1)')};
  }

  .icon-wrapper {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: ${props => (props.color ? `${props.color}10` : 'rgba(59, 130, 246, 0.1)')};
    color: ${props => props.color || '#3b82f6'};
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const MetricLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const MetricValue = styled.div`
  font-size: 1.25rem;
  font-weight: 800;
  color: #0f172a;
`;

const Unit = styled.span`
  font-size: 0.875rem;
  font-weight: 400;
  color: #94a3b8;
  margin-left: 0.25rem;
`;

const PaginationFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  padding-top: 1rem;
  flex-wrap: wrap;
`;

const PageButton = styled.button`
  padding: 0.625rem 1.25rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #0f172a;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.div`
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PageSelect = styled.select`
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: white;
  font-size: 0.875rem;
  color: #4a5568;
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: #3b82f6;
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #64748b;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  svg {
    width: 48px;
    height: 48px;
    opacity: 0.5;
  }
`;

function parseJSON<T>(s?: string | null): T | undefined {
  if (!s) return undefined;
  try { return JSON.parse(s) as T; } catch { return undefined; }
}

const MyVitals: React.FC = () => {
  const navigate = useNavigate();
  const logout = useLogout();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const cameras = useCameras();
  const pid = useMemo(() => localStorage.getItem(StorageKeys.PATIENT_ID) || '', []);
  const user = useMemo(() => {
    const userStr = localStorage.getItem(StorageKeys.AUTH_USER);
    return userStr ? JSON.parse(userStr) : null;
  }, []);
  const isOrgType3 = user?.org_type === 3;
  const orgId = user?.org_id;
  
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [expandedVitals, setExpandedVitals] = useState<Record<string, boolean>>({});

  const toggleExpand = (id: string) => {
    setExpandedVitals(prev => ({ ...prev, [id]: !prev[id] }));
  };
  const [getVitalPdf, { isFetching: isPdfFetching }] = useLazyGetVitalPdfQuery();
  const [sendToSabai, { isLoading: isSendingSabai }] = useSendToSabaiMutation();
  const [activeSabaiId, setActiveSabaiId] = useState<string | null>(null);

  const isSabaiConnected = useMemo(() => {
    const storedUser = localStorage.getItem(StorageKeys.AUTH_USER);
    const parsedUser = storedUser ? JSON.parse(storedUser) : null;
    return parsedUser?.sabai_status !== false && parsedUser?.sabai_status !== 'false';
  }, []);

  const [sabaiMessage, setSabaiMessage] = useState<{ [key: string]: { text: string; type: 'success' | 'error' } }>({});

  const handleTalkToSabai = async (vitalId: string) => {
    try {
      setActiveSabaiId(vitalId);
      const res = await sendToSabai(vitalId).unwrap();
      setSabaiMessage(prev => ({ 
        ...prev, 
        [vitalId]: { text: res?.message || 'Report sent to Sabai successfully!', type: 'success' } 
      }));
    } catch (error: any) {
      console.error('Failed to send to Sabai:', error);
      setSabaiMessage(prev => ({ 
        ...prev, 
        [vitalId]: { text: error?.data?.message || error?.message || 'Failed to send to Sabai.', type: 'error' } 
      }));
    } finally {
      setActiveSabaiId(null);
      setTimeout(() => {
        setSabaiMessage(prev => {
          const newMsgs = { ...prev };
          delete newMsgs[vitalId];
          return newMsgs;
        });
      }, 5000);
    }
  };

  const handleDownloadPdf = async (vitalId: string) => {
    try {
      const blob = await getVitalPdf(vitalId).unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Vital_Report_${vitalId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      alert('Failed to download PDF');
    }
  };

  const handleSharePdf = async (vitalId: string) => {
    try {
      const blob = await getVitalPdf(vitalId).unwrap();
      const file = new File([blob], `Vital_Report_${vitalId}.pdf`, { type: 'application/pdf' });
      
      if (navigator.share) {
        await navigator.share({
          title: 'Vital Report',
          text: 'Here is your vital assessment report.',
          files: [file],
        });
      } else {
        alert('Sharing is not supported on this device/browser. Please download the file instead.');
      }
    } catch (error) {
      console.error('Failed to share PDF:', error);
      alert('Failed to share PDF');
    }
  };

  const toggleSettings = useCallback(() => {
    setIsSettingsOpen(prev => !prev);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  const updateLicenseStatus = useCallback((valid: boolean) => {
    setIsLicenseValid(valid);
  }, []);

  const queryParams: any = {
    pageNo: page,
    pagesize: pageSize,
    pagination_required: true,
  };
  
  if (isOrgType3 && orgId) {
    queryParams.org_id = orgId;
  } else if (pid) {
    queryParams.patient_id = pid;
  }

  const shouldSkip = !(isOrgType3 && orgId) && !pid;

  const { data, isLoading, isError } = useGetAllVitalsQuery(queryParams, { skip: shouldSkip });

  useEffect(() => {
    updateLicenseStatus(true);
  }, [updateLicenseStatus]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh24 = d.getHours();
    const min = String(d.getMinutes()).padStart(2, '0');
    const period = hh24 >= 12 ? 'PM' : 'AM';
    const h12 = hh24 % 12 === 0 ? 12 : hh24 % 12;
    return `${mm}/${dd}/${yyyy} ${h12}:${min} ${period}`;
  };

  return (
    <PageContainer>
      <TopBar onSettingsClick={toggleSettings} isMeasuring={false} />
      <SettingsBars
        open={isSettingsOpen}
        onClose={handleCloseSettings}
        cameras={cameras}
        isLicenseValid={isLicenseValid}
      />
      <ContentWrapper>
        <GlassCard>
          <Header>
            <Title>Vitals History</Title>
            <Subtitle>Historical measurements and wellness assessment scores</Subtitle>
          </Header>

          {shouldSkip && (
            <NoDataMessage>
              <User />
              <p>{isOrgType3 ? 'No organization ID found.' : 'No patient selected. Please register a patient to view vitals history.'}</p>
            </NoDataMessage>
          )}

          {isLoading && (
            <NoDataMessage>
              <Activity className="animate-pulse" />
              <p>Fetching your health records...</p>
            </NoDataMessage>
          )}

          {isError && (
            <NoDataMessage>
              <Activity style={{ color: '#ef4444' }} />
              <p>Failed to load vitals data. Please refresh and try again.</p>
            </NoDataMessage>
          )}

          {data?.vitals?.length ? (
            <VitalsList>
              {data.vitals.map((v: any) => {
                const heartAge = parseJSON<{ value: number }>(v.HEART_AGE)?.value;
                const bloodPressure = parseJSON<{ value: { systolic: number; diastolic: number } }>(v.BLOOD_PRESSURE)?.value;
                const pulseRate = parseJSON<{ value: number }>(v.PULSE_RATE)?.value;
                const respirationRate = parseJSON<{ value: number }>(v.RESPIRATION_RATE)?.value;
                const hemoglobin = parseJSON<{ value: number }>(v.HEMOGLOBIN)?.value;
                const hemoglobinA1c = parseJSON<{ value: number }>(v.HEMOGLOBIN_A1C)?.value;
                const wellness = parseJSON<{ value: number }>(v.WELLNESS_INDEX)?.value;
                const stress = parseJSON<{ value: number }>(v.STRESS_LEVEL)?.value;

                const ascvdRisk = parseJSON<{ value: number }>(v.ASCVD_RISK)?.value;
                const highBpRisk = parseJSON<{ value: number }>(v.HIGH_BLOOD_PRESSURE_RISK)?.value;
                const highGlucoseRisk = parseJSON<{ value: number }>(v.HIGH_FASTING_GLUCOSE_RISK)?.value;
                const highHbA1cRisk = parseJSON<{ value: number }>(v.HIGH_HEMOGLOBIN_A1C_RISK)?.value;
                const highCholRisk = parseJSON<{ value: number }>(v.HIGH_TOTAL_CHOLESTEROL_RISK)?.value;
                const lfhf = parseJSON<{ value: number }>(v.LFHF)?.value;
                const lowHemoglobinRisk = parseJSON<{ value: number }>(v.LOW_HEMOGLOBIN_RISK)?.value;
                const meanRri = parseJSON<{ value: number }>(v.MEAN_RRI)?.value;
                const normalizedStress = parseJSON<{ value: number }>(v.NORMALIZED_STRESS_INDEX)?.value;
                const oxygenSat = parseJSON<{ value: number }>(v.OXYGEN_SATURATION)?.value;
                const pnsIndex = parseJSON<{ value: number }>(v.PNS_INDEX)?.value;
                const pnsZone = parseJSON<{ value: number }>(v.PNS_ZONE)?.value;
                const prq = parseJSON<{ value: number }>(v.PRQ)?.value;
                const rmssd = parseJSON<{ value: number }>(v.RMSSD)?.value;
                const sd1 = parseJSON<{ value: number }>(v.SD1)?.value;
                const sd2 = parseJSON<{ value: number }>(v.SD2)?.value;
                const sdnn = parseJSON<{ value: number }>(v.SDNN)?.value;
                const snsIndex = parseJSON<{ value: number }>(v.SNS_INDEX)?.value;
                const snsZone = parseJSON<{ value: number }>(v.SNS_ZONE)?.value;
                const stressIndex = parseJSON<{ value: number }>(v.STRESS_INDEX)?.value;
                const wellnessLevel = parseJSON<{ value: number }>(v.WELLNESS_LEVEL)?.value;

                return (
                  <VitalsItem key={v.id}>
                    <ItemHeader>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <ItemTitle>
                          <Gauge size={20} color="#3b82f6" />
                          <span>Assessment Result</span>
                        </ItemTitle>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <DateText>{formatDate(v.recorded_date)}</DateText>
                        {isSabaiConnected && (
                          <SabaiButton onClick={() => handleTalkToSabai(v.id)} disabled={isSendingSabai && activeSabaiId === v.id}>
                            {isSendingSabai && activeSabaiId === v.id ? 'Sending...' : 'Talk to Sabai'}
                          </SabaiButton>
                        )}
                        <ActionButton data-tooltip="Download report" onClick={() => handleDownloadPdf(v.id)} disabled={isPdfFetching}>
                          <Download size={18} />
                        </ActionButton>
                        <ActionButton data-tooltip="Share report" onClick={() => handleSharePdf(v.id)} disabled={isPdfFetching}>
                          <Share2 size={18} />
                        </ActionButton>
                      </div>
                    </ItemHeader>
                    {sabaiMessage[v.id] && (
                      <div style={{
                        padding: '0.75rem 1rem',
                        marginBottom: '1rem',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        backgroundColor: sabaiMessage[v.id].type === 'success' ? '#dcfce7' : '#fee2e2',
                        color: sabaiMessage[v.id].type === 'success' ? '#166534' : '#991b1b',
                        border: `1px solid ${sabaiMessage[v.id].type === 'success' ? '#bbf7d0' : '#fecaca'}`
                      }}>
                        {sabaiMessage[v.id].text}
                      </div>
                    )}

                    {v.DeviceUser && (
                      <div style={{ 
                        display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'center', 
                        marginBottom: '1.5rem', padding: '0.75rem 1.25rem', 
                        background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' 
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#334155', fontWeight: 500, fontSize: '0.875rem' }}>
                          <User size={16} color="#10b981" />
                          <span>{v.DeviceUser.name || '--'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#334155', fontWeight: 500, fontSize: '0.875rem' }}>
                          <Mail size={16} color="#f59e0b" />
                          <span>{v.DeviceUser.email || '--'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#334155', fontWeight: 500, fontSize: '0.875rem' }}>
                          <Phone size={16} color="#8b5cf6" />
                          <span>{v.DeviceUser.phone || '--'}</span>
                        </div>
                      </div>
                    )}

                    <MetricsGrid>
                      <MetricCard color="#3b82f6">
                        <div className="icon-wrapper"><Activity /></div>
                        <MetricLabel>Wellness</MetricLabel>
                        <MetricValue>{wellness ?? '--'}<Unit>/10</Unit></MetricValue>
                      </MetricCard>

                      <MetricCard color="#ef4444">
                        <div className="icon-wrapper"><Heart /></div>
                        <MetricLabel>Heart Age</MetricLabel>
                        <MetricValue>{heartAge ?? '--'}<Unit>yrs</Unit></MetricValue>
                      </MetricCard>

                      {bloodPressure && (
                        <MetricCard color="#8b5cf6">
                          <div className="icon-wrapper"><Heart /></div>
                          <MetricLabel>Blood Pressure</MetricLabel>
                          <MetricValue>
                            {bloodPressure.systolic}/{bloodPressure.diastolic}
                            <Unit>mmHg</Unit>
                          </MetricValue>
                        </MetricCard>
                      )}

                      <MetricCard color="#ef4444">
                        <div className="icon-wrapper"><Heart /></div>
                        <MetricLabel>Pulse Rate</MetricLabel>
                        <MetricValue>{pulseRate ?? '--'}<Unit>bpm</Unit></MetricValue>
                      </MetricCard>

                      <MetricCard color="#06b6d4">
                        <div className="icon-wrapper"><Wind /></div>
                        <MetricLabel>Respiration</MetricLabel>
                        <MetricValue>{respirationRate ?? '--'}<Unit>/min</Unit></MetricValue>
                      </MetricCard>

                      <MetricCard color="#f59e0b">
                        <div className="icon-wrapper"><Brain /></div>
                        <MetricLabel>Stress Level</MetricLabel>
                        <MetricValue>{stress ?? '--'}<Unit>/10</Unit></MetricValue>
                      </MetricCard>

                      <MetricCard color="#ef4444">
                        <div className="icon-wrapper"><Droplet /></div>
                        <MetricLabel>Hemoglobin</MetricLabel>
                        <MetricValue>{hemoglobin ?? '--'}<Unit>g/dL</Unit></MetricValue>
                      </MetricCard>

                      <MetricCard color="#f59e0b">
                        <div className="icon-wrapper"><Droplet /></div>
                        <MetricLabel>HbA1c</MetricLabel>
                        <MetricValue>{hemoglobinA1c ?? '--'}<Unit>%</Unit></MetricValue>
                      </MetricCard>
                    </MetricsGrid>

                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
                      <button 
                        onClick={() => toggleExpand(v.id)}
                        style={{ 
                          display: 'flex', alignItems: 'center', gap: '0.5rem', 
                          background: 'transparent', border: '1px solid #e2e8f0', color: '#3b82f6', 
                          fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
                          padding: '0.5rem 1rem', borderRadius: '8px', transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                        onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                      >
                        {expandedVitals[v.id] ? 'Hide Details' : 'View Details'}
                        {expandedVitals[v.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    </div>

                    {expandedVitals[v.id] && (
                      <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px dashed #e2e8f0' }}>
                        <h4 style={{ margin: '0 0 1.25rem 0', fontSize: '1rem', color: '#0f172a' }}>Detailed Assessment Records</h4>
                        <MetricsGrid>
                          <MetricCard color="#ef4444">
                            <div className="icon-wrapper"><Heart /></div>
                            <MetricLabel>ASCVD Risk</MetricLabel>
                            <MetricValue>{ascvdRisk ?? '--'}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#ef4444">
                            <div className="icon-wrapper"><Heart /></div>
                            <MetricLabel>High BP Risk</MetricLabel>
                            <MetricValue>{highBpRisk ?? '--'}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#06b6d4">
                            <div className="icon-wrapper"><Droplet /></div>
                            <MetricLabel>High Glucose Risk</MetricLabel>
                            <MetricValue>{highGlucoseRisk ?? '--'}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#f59e0b">
                            <div className="icon-wrapper"><Droplet /></div>
                            <MetricLabel>High HbA1c Risk</MetricLabel>
                            <MetricValue>{highHbA1cRisk ?? '--'}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#8b5cf6">
                            <div className="icon-wrapper"><Activity /></div>
                            <MetricLabel>High Chol Risk</MetricLabel>
                            <MetricValue>{highCholRisk ?? '--'}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#ef4444">
                            <div className="icon-wrapper"><Droplet /></div>
                            <MetricLabel>Low Hb Risk</MetricLabel>
                            <MetricValue>{lowHemoglobinRisk ?? '--'}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#3b82f6">
                            <div className="icon-wrapper"><Activity /></div>
                            <MetricLabel>LFHF</MetricLabel>
                            <MetricValue>{lfhf ?? '--'}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#ef4444">
                            <div className="icon-wrapper"><Heart /></div>
                            <MetricLabel>Mean RRI</MetricLabel>
                            <MetricValue>{meanRri ?? '--'}{meanRri != null && <Unit>ms</Unit>}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#f59e0b">
                            <div className="icon-wrapper"><Brain /></div>
                            <MetricLabel>Norm Stress Index</MetricLabel>
                            <MetricValue>{normalizedStress ?? '--'}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#06b6d4">
                            <div className="icon-wrapper"><Wind /></div>
                            <MetricLabel>Oxygen Saturation</MetricLabel>
                            <MetricValue>{oxygenSat ?? '--'}{oxygenSat ? <Unit>%</Unit> : ''}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#10b981">
                            <div className="icon-wrapper"><Brain /></div>
                            <MetricLabel>PNS Index</MetricLabel>
                            <MetricValue>{pnsIndex ?? '--'}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#10b981">
                            <div className="icon-wrapper"><Activity /></div>
                            <MetricLabel>PNS Zone</MetricLabel>
                            <MetricValue>{pnsZone ?? '--'}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#8b5cf6">
                            <div className="icon-wrapper"><Heart /></div>
                            <MetricLabel>PRQ</MetricLabel>
                            <MetricValue>{prq ?? '--'}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#3b82f6">
                            <div className="icon-wrapper"><Activity /></div>
                            <MetricLabel>RMSSD</MetricLabel>
                            <MetricValue>{rmssd ?? '--'}{rmssd != null && <Unit>ms</Unit>}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#6366f1">
                            <div className="icon-wrapper"><Activity /></div>
                            <MetricLabel>SD1</MetricLabel>
                            <MetricValue>{sd1 ?? '--'}{sd1 != null && <Unit>ms</Unit>}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#6366f1">
                            <div className="icon-wrapper"><Activity /></div>
                            <MetricLabel>SD2</MetricLabel>
                            <MetricValue>{sd2 ?? '--'}{sd2 != null && <Unit>ms</Unit>}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#6366f1">
                            <div className="icon-wrapper"><Activity /></div>
                            <MetricLabel>SDNN</MetricLabel>
                            <MetricValue>{sdnn ?? '--'}{sdnn != null && <Unit>ms</Unit>}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#f97316">
                            <div className="icon-wrapper"><Brain /></div>
                            <MetricLabel>SNS Index</MetricLabel>
                            <MetricValue>{snsIndex ?? '--'}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#f97316">
                            <div className="icon-wrapper"><Activity /></div>
                            <MetricLabel>SNS Zone</MetricLabel>
                            <MetricValue>{snsZone ?? '--'}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#ef4444">
                            <div className="icon-wrapper"><Brain /></div>
                            <MetricLabel>Stress Index</MetricLabel>
                            <MetricValue>{stressIndex ?? '--'}</MetricValue>
                          </MetricCard>
                          <MetricCard color="#10b981">
                            <div className="icon-wrapper"><Activity /></div>
                            <MetricLabel>Wellness Level</MetricLabel>
                            <MetricValue>{wellnessLevel ?? '--'}</MetricValue>
                          </MetricCard>
                        </MetricsGrid>
                      </div>
                    )}
                  </VitalsItem>
                );
              })}
            </VitalsList>
          ) : !isLoading && !isError && !shouldSkip ? (
            <NoDataMessage>
              <ClipboardList />
              <p>No vitals records found. Start a new scan to begin tracking your health.</p>
            </NoDataMessage>
          ) : null}

          <PaginationFooter>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <PageButton onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={isLoading || (data?.currentPage ?? 1) <= 1}>
                Previous
              </PageButton>
              <PageButton onClick={() => setPage((p) => Math.min(data?.totalPages || p, p + 1))} disabled={isLoading || (data?.currentPage ?? 1) >= (data?.totalPages ?? 1)}>
                Next
              </PageButton>
            </div>

            <PageInfo>
              <span>Page {data?.currentPage ?? page} of {data?.totalPages ?? 1}</span>
              <span style={{ opacity: 0.5 }}>|</span>
              <span>Total Records: {data?.totalCount ?? 0}</span>
            </PageInfo>

            <PageSelect
              value={pageSize}
              onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
            >
              <option value={5}>Show 5</option>
              <option value={10}>Show 10</option>
              <option value={20}>Show 20</option>
            </PageSelect>
          </PaginationFooter>
        </GlassCard>
      </ContentWrapper>
    </PageContainer>
  );
};

export default MyVitals;